import React, { useMemo, useState, useId } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { FiBarChart2, FiTrendingUp, FiChevronDown } from 'react-icons/fi';
import { AiOutlinePieChart } from 'react-icons/ai';
import './ChartBlock.css';

const CHART_COLORS = [
  '#22C55E', '#3B82F6', '#A855F7', '#F59E0B', '#EC4899',
  '#06B6D4', '#EF4444', '#10B981', '#8B5CF6', '#F97316',
  '#14B8A6', '#E879F9', '#84CC16', '#0EA5E9', '#FB7185',
];

const BAR_GRADIENTS = [
  ['#A78BFA', '#7C3AED'],
  ['#67E8F9', '#06B6D4'],
  ['#6EE7B7', '#10B981'],
  ['#FCD34D', '#F59E0B'],
  ['#F9A8D4', '#EC4899'],
  ['#A5B4FC', '#6366F1'],
  ['#5EEAD4', '#14B8A6'],
  ['#FDBA74', '#FB923C'],
  ['#93C5FD', '#3B82F6'],
  ['#FCA5A5', '#EF4444'],
];

export interface ChartConfig {
  type: 'bar' | 'line' | 'pie' | 'area' | 'donut' | 'horizontalBar';
  title?: string;
  data: Record<string, any>[];
  xKey?: string;
  yKeys?: string[];
  colors?: string[];
  stacked?: boolean;
}

function autoDetectKeys(data: Record<string, any>[]): { xKey: string; yKeys: string[] } {
  if (!data || data.length === 0) return { xKey: 'name', yKeys: ['value'] };
  const keys = Object.keys(data[0]);
  const numericKeys: string[] = [];
  const stringKeys: string[] = [];

  for (const key of keys) {
    const sample = data[0][key];
    if (typeof sample === 'number' || (!isNaN(Number(sample)) && sample !== '' && sample !== null)) {
      numericKeys.push(key);
    } else {
      stringKeys.push(key);
    }
  }

  const xKey = stringKeys[0] || keys[0];
  const yKeys = numericKeys.length > 0 ? numericKeys : keys.filter(k => k !== xKey);
  return { xKey, yKeys };
}

function formatValue(val: any): string {
  if (typeof val !== 'number') return String(val ?? '');
  if (Math.abs(val) >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
  return val % 1 === 0 ? val.toLocaleString() : val.toFixed(2);
}

function truncateLabel(label: string, max = 18): string {
  return label.length > max ? label.slice(0, max) + '…' : label;
}

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__label">{label}</p>
      <div className="chart-tooltip__divider" />
      {payload.map((entry: any, i: number) => (
        <div key={i} className="chart-tooltip__row">
          <span className="chart-tooltip__dot" style={{ background: entry.color }} />
          <span className="chart-tooltip__name">{entry.name}</span>
          <span className="chart-tooltip__val">{formatValue(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

const PieTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip__row">
        <span className="chart-tooltip__dot" style={{ background: entry.payload?.fill }} />
        <span className="chart-tooltip__name">{entry.name}</span>
        <span className="chart-tooltip__val">{formatValue(entry.value)}</span>
      </div>
    </div>
  );
};

const ChartBlock: React.FC<{ config: ChartConfig }> = ({ config }) => {
  const [expanded, setExpanded] = useState(false);
  const uid = useId();
  const { type, title, data, stacked } = config;
  const colors = config.colors || CHART_COLORS;

  const { xKey, yKeys } = useMemo(() => {
    if (config.xKey && config.yKeys) return { xKey: config.xKey, yKeys: config.yKeys };
    const auto = autoDetectKeys(data);
    return {
      xKey: config.xKey || auto.xKey,
      yKeys: config.yKeys || auto.yKeys,
    };
  }, [config, data]);

  const TypeIcon = type === 'pie' || type === 'donut' ? AiOutlinePieChart
    : type === 'line' ? FiTrendingUp
    : FiBarChart2;

  if (!data || data.length === 0) return null;

  const chartHeight = expanded ? 440 : 300;
  const gridStroke = 'rgba(139, 92, 246, 0.06)';
  const axisTickStyle = { fontSize: 10, fill: 'rgba(255,255,255,0.38)', fontFamily: 'Poppins, sans-serif' };

  const renderChart = () => {
    switch (type) {
      case 'bar':
      case 'horizontalBar':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={data}
              layout={type === 'horizontalBar' ? 'vertical' : 'horizontal'}
              margin={type === 'horizontalBar'
                ? { top: 8, right: 24, left: 12, bottom: 8 }
                : { top: 12, right: 12, left: -8, bottom: data.length > 6 ? 16 : 8 }
              }
              barCategoryGap="18%"
            >
              <defs>
                {yKeys.map((_, i) => {
                  const grad = BAR_GRADIENTS[i % BAR_GRADIENTS.length];
                  return (
                    <linearGradient key={`bg-${uid}-${i}`} id={`bg-${uid}-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={grad[0]} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={grad[1]} stopOpacity={0.7} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 6" stroke={gridStroke} vertical={type !== 'horizontalBar'} horizontal={type === 'horizontalBar' || true} />
              {type === 'horizontalBar' ? (
                <>
                  <XAxis type="number" tick={axisTickStyle} tickFormatter={formatValue} axisLine={false} tickLine={false} />
                  <YAxis dataKey={xKey} type="category" tick={axisTickStyle} width={120} axisLine={false} tickLine={false} tickFormatter={(v: string) => truncateLabel(v, 22)} />
                </>
              ) : (
                <>
                  <XAxis
                    dataKey={xKey}
                    tick={axisTickStyle}
                    interval={0}
                    angle={data.length > 5 ? -40 : 0}
                    textAnchor={data.length > 5 ? 'end' : 'middle'}
                    height={data.length > 5 ? 70 : 36}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v: string) => truncateLabel(v, 16)}
                  />
                  <YAxis tick={axisTickStyle} tickFormatter={formatValue} axisLine={false} tickLine={false} />
                </>
              )}
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(139, 92, 246, 0.08)' }} />
              {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.58)' }} />}
              {yKeys.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={yKeys.length === 1 ? undefined : `url(#bg-${uid}-${i})`}
                  radius={type === 'horizontalBar' ? [0, 6, 6, 0] : [6, 6, 0, 0]}
                  stackId={stacked ? 'stack' : undefined}
                  maxBarSize={48}
                >
                  {yKeys.length === 1 && data.map((_, di) => (
                    <Cell key={`cell-${di}`} fill={CHART_COLORS[di % CHART_COLORS.length]} />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <LineChart data={data} margin={{ top: 12, right: 12, left: -8, bottom: 8 }}>
              <defs>
                {yKeys.map((_, i) => {
                  const grad = BAR_GRADIENTS[i % BAR_GRADIENTS.length];
                  return (
                    <linearGradient key={`lg-${uid}-${i}`} id={`lg-${uid}-${i}`} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={grad[0]} />
                      <stop offset="100%" stopColor={grad[1]} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 6" stroke={gridStroke} />
              <XAxis dataKey={xKey} tick={axisTickStyle} axisLine={false} tickLine={false} tickFormatter={(v: string) => truncateLabel(v)} />
              <YAxis tick={axisTickStyle} tickFormatter={formatValue} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {yKeys.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={`url(#lg-${uid}-${i})`}
                  strokeWidth={2.5}
                  dot={{ r: 3.5, fill: colors[i % colors.length], strokeWidth: 0 }}
                  activeDot={{ r: 5, stroke: colors[i % colors.length], strokeWidth: 2, fill: '#0c0a1d' }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={data} margin={{ top: 12, right: 12, left: -8, bottom: 8 }}>
              <defs>
                {yKeys.map((_, i) => {
                  const grad = BAR_GRADIENTS[i % BAR_GRADIENTS.length];
                  return (
                    <React.Fragment key={`area-defs-${uid}-${i}`}>
                      <linearGradient id={`af-${uid}-${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={grad[0]} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={grad[1]} stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id={`as-${uid}-${i}`} x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={grad[0]} />
                        <stop offset="100%" stopColor={grad[1]} />
                      </linearGradient>
                    </React.Fragment>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 6" stroke={gridStroke} />
              <XAxis dataKey={xKey} tick={axisTickStyle} axisLine={false} tickLine={false} tickFormatter={(v: string) => truncateLabel(v)} />
              <YAxis tick={axisTickStyle} tickFormatter={formatValue} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              {yKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
              {yKeys.map((key, i) => (
                <Area key={key} type="monotone" dataKey={key} stroke={`url(#as-${uid}-${i})`} fill={`url(#af-${uid}-${i})`} strokeWidth={2} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie':
      case 'donut': {
        const valueKey = yKeys[0] || 'value';
        return (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <PieChart>
              <defs>
                {data.map((_, i) => {
                  const grad = BAR_GRADIENTS[i % BAR_GRADIENTS.length];
                  return (
                    <linearGradient key={`pie-${uid}-${i}`} id={`pie-${uid}-${i}`} x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={grad[0]} />
                      <stop offset="100%" stopColor={grad[1]} />
                    </linearGradient>
                  );
                })}
              </defs>
              <Pie
                data={data}
                dataKey={valueKey}
                nameKey={xKey}
                cx="50%"
                cy="50%"
                innerRadius={type === 'donut' ? '42%' : 0}
                outerRadius="72%"
                paddingAngle={3}
                strokeWidth={0}
                label={({ name, percent }: any) => `${truncateLabel(name || '', 14)} ${((percent ?? 0) * 100).toFixed(0)}%`}
                labelLine={{ stroke: 'rgba(255,255,255,0.15)', strokeWidth: 1 }}
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={`url(#pie-${uid}-${i})`} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 10, fontFamily: 'Poppins, sans-serif', color: 'rgba(255,255,255,0.58)', paddingTop: '8px' }}
                iconType="circle"
                iconSize={8}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={`chart-block ${expanded ? 'chart-block--expanded' : ''}`}>
      <div className="chart-block__header">
        <div className="chart-block__icon-wrap">
          <TypeIcon size={13} />
        </div>
        <span className="chart-block__title">{title || 'Chart'}</span>
        <button
          className="chart-block__toggle"
          onClick={() => setExpanded(e => !e)}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <FiChevronDown
            size={14}
            className={`chart-block__chevron ${expanded ? 'chart-block__chevron--open' : ''}`}
          />
        </button>
      </div>
      <div className="chart-block__body">
        {renderChart()}
      </div>
    </div>
  );
};

export default React.memo(ChartBlock);
